"use client"

import { ListPageHeader, ListPageTabs, RowActionsMenu, StatusCell, type RowActionItem } from "@/(admin)/components/list"
import { applyViewConfig, columnScrollX, ConfigureViewButton, useViewConfig } from "@/(admin)/components/view-config"
import AccessDenied from "@/components/custom-access-denied"
import { PAGES } from "@/constants/admin-paths"
import { useCheckPermission } from "@/hook/use-check-permission"
import { useAdminDomainFilter } from "@/hook/useAdminDomainFilter"
import { useDomainStore } from "@/stores/use-domain-store"
import { t } from "@/languages/i18n"
import { createPath } from "@/utils/create-path"
import { purgeRemovedPagePaths } from "@/validate/validatePages"
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  GlobalOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons"
import type { MenuProps, TableColumnsType, TableProps } from "antd"
import { App, Button, Card, Select, Space, Table } from "antd"
import Image from "next/image"
import Link from "next/link"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import queryString from "query-string"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { Images } from "../../../../constants/images"
import { normalizeText } from "../../../../lib/external-components"
import "../../../../styles/globals.css"
import { formatDatePost } from "../../../../utils/formatDate"
import Filters, { FilterComponent } from "../../../components/filters"
import { RootState } from "../../auth/services/redux/store"
import { deleteTemplate } from "../../block-content/service/blockContent"
import { AttributesPage, PageData, PaginationMeta } from "../../ui-builder/config/types"
import { useUiBuilder } from "../../ui-builder/services/ui-builder"
import pageService from "../services/page"
import { pageColumnsRegistry } from "./columns-registry"

export const domainOptions = [
  { value: "tamdagroup.eu", label: "tamdagroup.eu" },
  { value: "tamdafoods.eu", label: "tamdafoods.eu" },
  { value: "tamdaoc.eu", label: "tamdaoc.eu" },
  { value: "tamdamedia.eu", label: "tamdamedia.eu" },
  { value: "tamdaexpress.eu", label: "tamdaexpress.eu" },
]

const List: React.FC = () => {
  const [dataItem, setDataItem] = useState<PageData[]>([])
  const filterRef = useRef<{ filterFunction: () => void } | null>(null)
  const [sortPaginationValues, setSortPaginationValues] = useState<{ [key: string]: string } | null>(null)
  const { modal, message } = App.useApp()
  const [needRefresh, setNeedRefresh] = useState<boolean>(false)
  const [filterValues, setFilterValues] = useState<{ [key: string]: string } | null>(null)
  const [defaultFilterValues, setDefaultFilterValues] = useState<{ [key: string]: any } | null>(null)

  const [pageNameOptions, setPageNameOptions] = useState<{ value: string | number; label: string }[]>([])
  const [filterTabPage, setFilterTabPage] = useState("all")
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])

  const [bulkAction, setBulkAction] = useState<string>("")
  const router = useRouter()
  // tamdamedia.eu chỉ có tiếng Việt → ẩn chức năng dịch sang EN/CS.
  const isTamdaMedia = useDomainStore(state => state.selectedDomain?.attributes?.domain === "tamdamedia.eu")
  const [loading, setLoading] = useState(false)

  const [metaData, setMetaData] = useState<PaginationMeta>()

  const [pageItemData, setPageItemData] = useState<PageData>()
  const account = useSelector((state: RootState) => state.account)
  const { hasPermission: checkPermission } = useCheckPermission()

  const pageServices = pageService()
  const { createNewBlockContent } = useUiBuilder()
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const { domainFilterOptions, showDomainFilter, enforcedDomain, isDomainStoreReady } = useAdminDomainFilter(
    t("All Domains"),
  )

  const {
    config: viewConfig,
    setConfig: setViewConfig,
    reset: resetViewConfig,
    defaultConfig: defaultViewConfig,
  } = useViewConfig("page", pageColumnsRegistry, { pageSize: 20 })

  const supportedLocales = useMemo(
    () => [
      { value: "vi", label: t("VI"), logoSrc: Images.logoVi },
      { value: "en", label: t("EN"), logoSrc: Images.logoEn },
      { value: "cs", label: "CS", logoSrc: Images.logoCs },
    ],
    [t],
  )

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(selectedRowKeys)
    },
  }

  const fetchAllPages = async (searchTerms?: { [key: string]: string }) => {
    setLoading(true)
    setDataItem([])

    try {
      const normalizedSearchTerms = {
        ...searchTerms,
        ...(enforcedDomain ? { domain: enforcedDomain } : {}),
      }
      const defaultPagination = { page: "1", pageSize: "20" }
      const allResponse = await pageServices.fetchAllPages({ ...defaultPagination, ...normalizedSearchTerms })
      const response = allResponse && allResponse.data
      const meta = allResponse && allResponse.meta
      setMetaData(meta)

      if (response && response.length > 0) {
        setDataItem(response)
        setPageNameOptions(
          response.map((page: { id: number; attributes: AttributesPage }) => ({
            value: page.id,
            label: page.attributes.name,
          })),
        )

        setLoading(false)
      } else {
        setLoading(false)
      }

      return response
    } catch (error) {
      message.error(t("Failed to fetch page. Please try again"))
    }
  }

  const onSearch = useCallback(
    (filters: object) => {
      const values: { [key: string]: string } = {}

      Object.keys(filters).forEach(key => {
        const value = filters[key as keyof typeof filters]
        if (typeof value === "string") {
          values[key] = value
        }
      })

      if (!values) {
        message.error(t("Values ​​are not defined"))
        return
      }

      let newValue = { ...values }

      if (values.search) {
        newValue = { ...newValue, search: normalizeText(values.search) }
      }

      if (enforcedDomain) {
        newValue = { ...newValue, domain: enforcedDomain }
      }

      // Default locale is "all" (see URL parse + fetch); omit from query so URL stays minimal when user only filters e.g. domain.
      if (newValue.locale === "all") {
        delete newValue.locale
      }

      const query = queryString.stringify(newValue, { encode: false })
      const newUrl = query.length > 0 ? `${pathname}?${query}` : pathname
      const currentUrl = searchParams.toString().length > 0 ? `${pathname}?${searchParams.toString()}` : pathname

      if (newUrl === currentUrl) return

      // App Router: useSearchParams only updates after router navigation — replaceState does not refetch/sync URL state.
      router.replace(newUrl, { scroll: false })
    },
    [pathname, enforcedDomain, searchParams, router],
  )

  useEffect(() => {
    const parsedParams = queryString.parse(searchParams.toString())
    const newFilterValues: { [key: string]: string } = {}
    const newSortPaginationValues: { [key: string]: string } = {}
    const sortPaginationKeys = ["page", "pageSize", "limit", "start", "sort"]

    for (const key in parsedParams) {
      const value = parsedParams[key]
      if (typeof value === "string") {
        if (sortPaginationKeys.includes(key)) {
          newSortPaginationValues[key] = value
        } else {
          newFilterValues[key] = value
        }
      }
    }

    if (!newFilterValues.locale) {
      newFilterValues.locale = "all"
    }

    if (enforcedDomain) {
      newFilterValues.domain = enforcedDomain
    }

    const nextSortKey = JSON.stringify(newSortPaginationValues)
    const nextFilterKey = JSON.stringify(newFilterValues)

    setSortPaginationValues(prev => {
      if (prev === null) return newSortPaginationValues
      const prevKey = JSON.stringify(prev)
      return prevKey === nextSortKey ? prev : newSortPaginationValues
    })

    setFilterValues(prev => {
      if (prev === null) return newFilterValues
      const prevKey = JSON.stringify(prev)
      return prevKey === nextFilterKey ? prev : newFilterValues
    })

    setDefaultFilterValues(prev => {
      if (prev === null) return newFilterValues
      const prevKey = JSON.stringify(prev)
      return prevKey === nextFilterKey ? prev : newFilterValues
    })
  }, [searchParams, enforcedDomain])

  useEffect(() => {
    if (!isDomainStoreReady) return
    if (!filterValues && !sortPaginationValues) return

    const params: { [key: string]: string } = {
      ...filterValues,
      ...sortPaginationValues,
      publicationState: filterTabPage,
    }

    if (!params.locale) {
      params.locale = "all"
    }
    if (!params.sort && viewConfig.sortField) {
      params.sort = `${viewConfig.sortField}:${viewConfig.sortOrder ?? "desc"}`
    }
    if (!params.pageSize) {
      params.pageSize = String(viewConfig.pageSize)
    }

    fetchAllPages(params)
  }, [filterValues, sortPaginationValues, filterTabPage, isDomainStoreReady, viewConfig])

  const handleEdit = (id: number) => {
    router.push(`/admin/pages/edit?id=${id}`)
  }

  const isSlugUnique = async (slug: string): Promise<boolean> => {
    try {
      const response = await pageServices.checkSlug(slug)
      return response.data.length === 0
    } catch {
      return false
    }
  }

  const generateUniqueSlug = async (baseSlug: string, baseName: string): Promise<{ slug: string; name: string }> => {
    let newSlug = baseSlug
    let newName = baseName
    let count = 1

    const match = newSlug.match(/-copy-(\d+)$/)
    if (match) {
      count = parseInt(match[1], 10) + 1
      newSlug = `${baseSlug.replace(/-copy-(\d+)$/, "")}-copy-${count}`
      newName = `${baseName.replace(/-copy-(\d+)$/, "")}-copy-${count}`
    } else {
      newSlug = `${baseSlug}-copy-1`
      newName = `${baseName}-copy-1`
    }

    while (!(await isSlugUnique(newSlug))) {
      count++
      newSlug = `${baseSlug.replace(/-copy-\d*$/, "")}-copy-${count}`
      newName = `${baseName.replace(/-copy-\d*$/, "")}-copy-${count}`
    }

    return { slug: newSlug, name: newName }
  }

  const handleDuplicate = async (id: number) => {
    const pageItemData = await pageServices.fetchPageById(id)

    if (pageItemData) {
      const blockContentData = pageItemData.attributes.blockContent.data?.attributes
      const baseSlug = pageItemData.attributes.slug
      const baseName = pageItemData.attributes.name
      const { slug: uniqueSlug, name: uniqueName } = await generateUniqueSlug(baseSlug, baseName)
      try {
        const createdBlockContent = await createNewBlockContent(blockContentData)
        const dataPayload = {
          name: uniqueName,
          slug: uniqueSlug,
          content: pageItemData?.attributes.content,
          publishedAt: pageItemData?.attributes.publishedAt
            ? new Date(pageItemData.attributes.publishedAt).toISOString()
            : null,
          image: pageItemData?.attributes.image.data || null,
          seo: {
            id: 0,
            metaTitle: pageItemData?.attributes.seo?.metaTitle,
            metaDescription: pageItemData?.attributes.seo?.metaDescription,
            canonicalURL: pageItemData?.attributes.seo?.canonicalURL,
            keywords: pageItemData?.attributes.seo?.keywords,
            metaRobots: pageItemData?.attributes.seo?.metaRobots,
            metaViewport: pageItemData?.attributes.seo?.metaViewport,
            structuredData: pageItemData?.attributes.seo?.structuredData,
          },
          isUseUIBuilder: pageItemData.attributes.isUseUIBuilder,
          blockContent: createdBlockContent?.data.id,
          domain: pageItemData.attributes?.domain,
          locale: pageItemData.attributes?.locale,
        }

        await pageServices.addPage({ data: dataPayload })
        message.success({ content: t("Page duplicated successfully!"), duration: 2 })
        setPageItemData(undefined)
        fetchAllPages()
      } catch (error) {
        message.error(t("Failed to duplicate page. Please try again."))
      }
    }
  }

  const handleLocaleAction = useCallback(
    (record: PageData, locale: string) => {
      if (!locale) return

      if (record.attributes.locale === locale) {
        router.push(`/admin/pages/edit?id=${record.id}`)
        return
      }

      const localization = record.attributes.localizations?.data?.find(item => item.attributes.locale === locale)

      if (localization) {
        router.push(`/admin/pages/edit?id=${localization.id}`)
        return
      }

      router.push(`/admin/pages/translate?id=${record.id}&locale=${locale}`)
    },
    [router],
  )

  const buildLanguageMenu = useCallback(
    (record: PageData): MenuProps => {
      const existingLocales = new Set<string>()

      if (record.attributes.locale) {
        existingLocales.add(record.attributes.locale)
      }

      record.attributes.localizations?.data?.forEach(item => {
        if (item.attributes.locale) {
          existingLocales.add(item.attributes.locale)
        }
      })

      const items: MenuProps["items"] = supportedLocales.map(option => {
        const hasTranslation = existingLocales.has(option.value)
        const statusLabel = hasTranslation ? t("Edit") : t("Create")

        return {
          key: option.value,
          label: (
            <div className="flex w-full items-center justify-between gap-3">
              <span className="flex items-center gap-1">
                <Image src={option.logoSrc} alt={option.label} className="h-5 w-5" />
                <span>{option.label}</span>
              </span>
              <span className="text-xs text-gray-500">{statusLabel}</span>
            </div>
          ),
        }
      })

      return {
        items,
        onClick: ({ key }) => handleLocaleAction(record, key as string),
      }
    },
    [handleLocaleAction, supportedLocales, t],
  )

  const handleDelete = async (id: number, pageName?: string) => {
    modal.confirm({
      title: t("Xoá trang này?"),
      content: pageName ? `${t("Bạn có chắc chắn muốn xóa trang")} "${pageName}"?` : t("Hành động không thể hoàn tác."),
      okText: t("Xoá"),
      okButtonProps: { danger: true },
      cancelText: t("Huỷ"),
      onOk: async () => {
        try {
          setLoading(true)
          const res = await pageServices.fetchPageById(id)
          // Chụp path TRƯỚC khi xoá để purge cứng URL (đã biến mất) → 404 ngay.
          // Chỉ purge khi đã publish — bản nháp chưa từng public, không có cache.
          const deletedPath = res?.attributes?.publishedAt ? (res?.attributes?.path ?? "") : ""
          if (res.attributes.blockContent.data) {
            await deleteTemplate(res.attributes.blockContent.data.id)
          }
          await pageServices.deletePage(id)
          if (deletedPath) await purgeRemovedPagePaths([deletedPath])
          fetchAllPages()
          message.success(t("The page has been successfully deleted!"))
        } catch {
          message.error(t("Handle Delete Error"))
        }
      },
    })
  }

  const handleBulkDelete = async () => {
    try {
      setLoading(true)
      const ids = selectedRowKeys.map(id => Number(id))
      // Chụp path (đã publish) trước khi xoá — 1 query cho cả lô, tránh N+1.
      const deletedPaths = await pageServices.fetchPublishedPathsByIds(ids)
      await Promise.all(ids.map(id => pageServices.deletePage(id)))
      if (deletedPaths.length) await purgeRemovedPagePaths(deletedPaths)
      setSelectedRowKeys([])
      fetchAllPages()
      setLoading(false)
      message.success(t("Pages deleted successfully!"))
    } catch {
      message.error(t("Handle Delete Error"))
    }
  }

  const handleApplyBulkAction = () => {
    if (bulkAction === "delete" && selectedRowKeys.length > 0) {
      modal.confirm({
        title: t("Confirm Delete"),
        content: t("Are you sure you want to delete the selected pages?"),
        okText: t("Delete"),
        cancelText: t("Cancel"),
        onOk: handleBulkDelete,
      })
    } else if (selectedRowKeys.length === 0) {
      message.warning(t("Please select at least one page to delete."))
    } else {
      message.warning(t("Please select a valid bulk action"))
    }
  }

  const handleChangeTab = (key: string) => {
    setFilterTabPage(key)

    setSortPaginationValues(null)

    const params: { [key: string]: string } = {
      ...(filterValues ?? defaultFilterValues ?? {}),
      publicationState: key,
    }

    fetchAllPages(params)
  }

  const onChange: TableProps<PageData>["onChange"] = (pagination, filters, sorter) => {
    const sortParams: { [key: string]: string } = {
      page: pagination.current?.toString() || "1",
      pageSize: pagination.pageSize?.toString() || "20",
    }

    if (sorter && !Array.isArray(sorter) && sorter.field && sorter.order) {
      const field = Array.isArray(sorter.field) ? sorter.field[1] : sorter.field
      const order = sorter.order === "ascend" ? "asc" : "desc"
      sortParams.sort = `${field}:${order}`
    }

    setSortPaginationValues(sortParams)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const onRefresh = () => {
    if (filterRef.current) {
      filterRef.current.filterFunction()
    }
    setSortPaginationValues(null)
    setNeedRefresh(!needRefresh)
  }

  const columns: TableColumnsType<PageData> = [
    {
      title: <strong>{t("Name")}</strong>,
      dataIndex: ["attributes", "name"],
      width: 280,
      key: "name",
      ellipsis: true,
      showSorterTooltip: false,
      sorter: true,
      render: (_, record) => (
        <Link
          href={`/admin/pages/edit?id=${record.id}`}
          className="text-[#1677ff] cursor-pointer transition duration-300 hover:text-[#0b5ed7] hover:underline text-base"
        >
          {record.attributes.name}
        </Link>
      ),
    },
    {
      title: <strong>{t("Domain")}</strong>,
      dataIndex: ["attributes", "domain"],
      width: 180,
      key: "content",
      ellipsis: true,
      sorter: true,
      showSorterTooltip: false,
      render: domain => {
        if (!domain) return <div>{t("—")}</div>

        return (
          <div>
            <div>{domain}</div>
          </div>
        )
      },
    },
    {
      title: <strong>{t("Language")}</strong>,
      dataIndex: ["attributes", "locale"],
      width: 220,
      key: "Language",
      hidden: false,
      sorter: true,
      showSorterTooltip: false,
      render: (_, record) => {
        const locale = record.attributes.locale
        if (locale) {
          const { logoSrc, label } = (() => {
            switch (locale) {
              case "vi":
                return { logoSrc: Images.logoVi, label: t("Tiếng Việt") }
              case "cs":
                return { logoSrc: Images.logoCs, label: t("Tiếng Séc") }
              case "en":
              default:
                return { logoSrc: Images.logoEn, label: t("Tiếng Anh") }
            }
          })()

          return (
            <div className="flex items-center gap-2">
              <Image src={logoSrc} alt="languages" className="w-5 h-5" />
              <span>{label}</span>
              {Array.isArray(record.attributes.localizations?.data) &&
                record.attributes.localizations?.data.length > 0 && (
                  <span>
                    (
                    {record.attributes.localizations.data
                      .map((item: PageData) => item.attributes.locale?.toUpperCase())
                      .join(", ")}
                    )
                  </span>
                )}
            </div>
          )
        }
      },
    },
    {
      title: <strong>{t("Create At")}</strong>,
      dataIndex: ["attributes", "createdAt"],
      width: 190,
      key: "createdAt",
      showSorterTooltip: false,
      sorter: true,
      render: (createdAt: string, record: PageData) => {
        const formattedDate = formatDatePost(createdAt)
        return (
          <span className="font-normal">
            {record.attributes.publishedAt ? t("Published") : t("Last Modified")}
            <br />
            <span>{formattedDate}</span>
          </span>
        )
      },
    },
    {
      title: t("Trạng thái"),
      dataIndex: ["attributes", "publishedAt"],
      key: "status",
      width: 130,
      sorter: true,
      showSorterTooltip: false,
      render: (publishedAt: string) => <StatusCell status={publishedAt ? "published" : "draft"} />,
    },
    {
      title: t("Hành động"),
      key: "action",
      width: 140,
      align: "right",
      render: (_, record) => {
        const languageMenu = buildLanguageMenu(record)
        const languageItems: RowActionItem[] = (languageMenu.items || []).map((item: any) => ({
          key: String(item.key),
          label: item.label,
          onClick: () => languageMenu.onClick?.({ key: String(item.key) } as any),
        }))

        const actionItems: RowActionItem[] = [
          ...(checkPermission("page", "create")
            ? [
                {
                  key: "duplicate",
                  label: t("Nhân bản"),
                  icon: <CopyOutlined />,
                  onClick: () => handleDuplicate(record.id),
                } as RowActionItem,
              ]
            : []),
          ...(!isTamdaMedia && languageItems.length > 0 && checkPermission("page", "createLocalization")
            ? [
                {
                  key: "translate",
                  label: t("Ngôn ngữ"),
                  icon: <GlobalOutlined />,
                  children: languageItems,
                } as RowActionItem,
              ]
            : []),
          ...(checkPermission("page", "delete")
            ? [
                { key: "divider", type: "divider" as const, label: "" },
                {
                  key: "delete",
                  label: t("Xoá"),
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: () => handleDelete(record.id, record.attributes.name),
                } as RowActionItem,
              ]
            : []),
        ]

        return (
          <RowActionsMenu
            primary={
              checkPermission("page", "update")
                ? {
                    label: t("Sửa"),
                    icon: <EditOutlined />,
                    onClick: () => handleEdit(record.id),
                  }
                : undefined
            }
            items={actionItems}
            ariaLabel={`Hành động cho ${record.attributes?.name || "trang"}`}
          />
        )
      },
    },
  ]

  if (!checkPermission("page", "find")) {
    return <AccessDenied />
  }

  const displayColumns = applyViewConfig(columns, pageColumnsRegistry, viewConfig)
  // Auto-scale: table scrolls horizontally to fit whatever columns are shown, so
  // added columns never squeeze existing cells (each cell stays on one line).
  const scrollX = columnScrollX(displayColumns, pageColumnsRegistry)

  const total = metaData?.pagination?.total ?? 0

  return (
    <div>
      <ListPageHeader
        title={t("Trang")}
        subtitle={total > 0 ? `${total} ${t("Trang")}` : undefined}
        primaryAction={
          checkPermission("page", "create") && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => router.push(createPath(PAGES.CREATE.PATH, searchParams))}
            >
              {t("Tạo trang mới")}
            </Button>
          )
        }
      />
      <ListPageTabs
        activeKey={filterTabPage}
        onChange={handleChangeTab}
        items={[
          { key: "all", label: t("Tất cả") },
          { key: "draft", label: t("Bản nháp") },
          { key: "published", label: t("Đã xuất bản") },
        ]}
      />
      <div className="items-center">
        <Space className=" space-x-5 flex justify-between">
          {viewConfig.enableBulk ? (
            <Space className="space-x-3">
              <Select
                defaultValue={t("Bulk Actions")}
                style={{ width: 170 }}
                onChange={value => setBulkAction(value)}
                options={[
                  { value: "all", label: t("Bulk Actions") },
                  { value: "delete", label: t("Delete") },
                ]}
              />
              <Button onClick={handleApplyBulkAction}>{t("Apply")}</Button>
            </Space>
          ) : (
            <span />
          )}
          <Space>
            <Button onClick={onRefresh} icon={<ReloadOutlined />}>
              {t("Refresh")}
            </Button>
            <ConfigureViewButton
              registry={pageColumnsRegistry}
              config={viewConfig}
              defaultConfig={defaultViewConfig}
              setConfig={setViewConfig}
              reset={resetViewConfig}
              features={{ search: false, filters: true, bulk: true }}
              pageSizeOptions={[10, 20, 50, 100]}
            />
          </Space>
        </Space>
      </div>
      {viewConfig.enableFilters && (
        <Card type="inner" style={styles.card}>
          <Filters
            filterRef={filterRef}
            defaultFilterValues={defaultFilterValues}
            onSubmit={onSearch}
            filters={
              [
                {
                  label: t("Name"),
                  placeholder: t("Find Page Name"),
                  name: "name",
                  type: "text",
                  options: pageNameOptions,
                  customStyle: "2xl:!w-[400px] !w-[300px]",
                },
                ...(showDomainFilter
                  ? [
                      {
                        label: t("Domain"),
                        placeholder: t("Find Domain"),
                        name: "domain",
                        type: "select",
                        options: domainFilterOptions,
                        customStyle: "!w-[180px]",
                      } as FilterComponent,
                    ]
                  : []),
                {
                  label: t("Locale"),
                  placeholder: t("Find Locale"),
                  name: "locale",
                  type: "select",
                  options: [
                    { value: "all", label: t("Tất cả ngôn ngữ") },
                    { value: "vi", label: t("Tiếng Việt") },
                    { value: "en", label: t("Tiếng Anh") },
                    { value: "cs", label: t("Tiếng Séc") },
                  ],
                  customStyle: "!w-[145px]",
                },
              ] as FilterComponent[]
            }
          />
        </Card>
      )}
      <Table
        className="mt-3  border border-[rgb(229 229 229)]"
        rowKey="id"
        rowSelection={viewConfig.enableBulk ? rowSelection : undefined}
        columns={displayColumns}
        loading={loading}
        dataSource={dataItem}
        onChange={onChange}
        pagination={{
          current: Number(sortPaginationValues?.page) || 1,
          pageSize: Number(sortPaginationValues?.pageSize) || viewConfig.pageSize,
          total: metaData?.pagination?.total || 0,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          locale: { items_per_page: "/ page" },
        }}
        rowClassName={(record, index) => (index % 2 === 0 ? "bg-[#f6f7f7] " : "bg-white")}
        scroll={{ x: scrollX }}
      />
    </div>
  )
}

export default List
const styles = {
  title: {
    marginTop: 0,
    marginBottom: 0,
  },
  card: {
    marginTop: "1.25rem",
    marginBottom: "1.25rem",
  },
}
